import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { Trophy, PartyPopper, ChevronRight, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";

export default function TrophyCase() {
  const [closedProjects, setClosedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClosedProjects();
  }, []);

  const fetchClosedProjects = async () => {
    try {
      // Get programs closed in the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const res = await apiClient.get(`/programs?stage=Closed&page_size=10`);
      
      // Filter to only recently closed (updated in last 7 days)
      const recentlyClosed = (res.data.data || res.data || []).filter(p => {
        const updatedAt = new Date(p.updated_at || p.last_activity_at);
        return updatedAt >= sevenDaysAgo;
      });
      
      setClosedProjects(recentlyClosed);
    } catch (err) {
      console.error("Failed to fetch closed projects", err);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return null; // Don't show loading state, just hide until ready
  }

  if (closedProjects.length === 0) {
    return null; // Don't show if no closed projects
  }

  return (
    <Card 
      data-testid="trophy-case" 
      className="border-border overflow-hidden"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-amber-100">
              <Trophy className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
            </div>
            <div>
              <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-amber-800">
                Trophy Case
              </CardTitle>
              <p className="text-[10px] text-amber-600">Completed this week</p>
            </div>
          </div>
          <Badge className="rounded-full bg-amber-100 text-amber-700 border-amber-200 font-semibold">
            {closedProjects.length} 🎉
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-1">
        {closedProjects.slice(0, 5).map((project, idx) => (
          <Link
            key={project.id}
            to={`/programs/${project.id}`}
            className="flex items-center justify-between py-3 px-1 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
            data-testid={`trophy-project-${project.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-lg">{idx === 0 ? "🏆" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : "⭐"}</span>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate group-hover:text-amber-700 transition-colors">
                  {project.program_name}
                </p>
                <p className="text-xs text-muted-foreground truncate">{project.client_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-amber-600 font-medium">
                {formatDate(project.updated_at || project.last_activity_at)}
              </span>
              <ChevronRight className="w-4 h-4 text-amber-400 group-hover:text-amber-600 transition-colors" />
            </div>
          </Link>
        ))}
        
        {closedProjects.length > 5 && (
          <Link
            to="/programs?stage=Closed"
            className="block text-center text-xs text-amber-600 hover:text-amber-800 pt-2 transition-colors"
          >
            View all {closedProjects.length} completed projects →
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
